import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchOrdersUiPreferences, saveOrdersUiPreferences } from '../services/ordersDailyTruthClient';
import {
  applyOrdersUiPatch,
  DEFAULT_CARD_HEADLINE,
  defaultOrdersUiPreferences,
  mergeOrdersUiPatches,
  type OrdersUiPreferences,
  type OrdersUiPreferencesPatch,
} from '../types/ordersUiPreferences';

/** Several quick changes (a reorder, a toggle) go up as one request. */
const SAVE_DEBOUNCE_MS = 400;

export type OrdersUiPreferencesStatus = 'loading' | 'ready' | 'error';

/**
 * The user's saved Orders page layout. Held on their profile, so it follows
 * them across devices; until it loads, or if it cannot, the first-visit
 * defaults apply and `status` says so. A change shows at once and is saved in
 * the background — if saving fails the layout still holds for this visit and
 * `saveError` says it will not be remembered.
 *
 * Demo mode keeps the layout in memory only: there is no profile to save to.
 */
export function useOrdersUiPreferences(opts?: { demo?: boolean }) {
  const demo = opts?.demo === true;
  const [prefs, setPrefs] = useState<OrdersUiPreferences>(defaultOrdersUiPreferences);
  const [status, setStatus] = useState<OrdersUiPreferencesStatus>(demo ? 'ready' : 'loading');
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // A change made before the saved layout arrived must not be overwritten by it.
  const touchedRef = useRef(false);
  const pendingRef = useRef<OrdersUiPreferencesPatch | null>(null);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (demo) return undefined;
    const ac = new AbortController();
    fetchOrdersUiPreferences({ signal: ac.signal })
      .then((res) => {
        if (ac.signal.aborted) return;
        if (!touchedRef.current) {
          setPrefs({
            section_order: res.section_order,
            sections_open: res.sections_open,
            eta_warning_minutes: res.eta_warning_minutes,
            // A server older than this option sends none.
            card_headline: res.card_headline ?? DEFAULT_CARD_HEADLINE,
          });
        }
        setSaved(res.saved);
        setStatus('ready');
      })
      .catch((err: unknown) => {
        if (ac.signal.aborted) return;
        console.warn('Orders layout could not be loaded', err);
        setStatus('error');
      });
    return () => ac.abort();
  }, [demo]);

  const flush = useCallback(async () => {
    timerRef.current = null;
    const patch = pendingRef.current;
    pendingRef.current = null;
    if (!patch || demo) return;
    try {
      await saveOrdersUiPreferences(patch);
      setSaved(true);
      setSaveError(null);
    } catch (err) {
      console.warn('Orders layout could not be saved', err);
      setSaveError('Your layout could not be saved, so it will not be remembered next time.');
    }
  }, [demo]);

  const update = useCallback((patch: OrdersUiPreferencesPatch) => {
    touchedRef.current = true;
    setPrefs((current) => applyOrdersUiPatch(current, patch));
    if (demo) return;
    pendingRef.current = pendingRef.current ? mergeOrdersUiPatches(pendingRef.current, patch) : patch;
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => { void flush(); }, SAVE_DEBOUNCE_MS);
  }, [demo, flush]);

  // Leaving the page must not drop a change still waiting out its debounce.
  useEffect(() => () => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      void flush();
    }
  }, [flush]);

  return { prefs, status, saved, saveError, update };
}
